import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Loader2, X, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "rounded-xl border border-neutral-700 bg-black px-4 py-3 text-sm text-stone-100 outline-none focus:border-white";

function ServiceCheckoutModal({ svc, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/services/checkout", { ...form, service_id: svc.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-4 backdrop-blur-sm sm:items-center" onClick={onClose} data-testid="service-checkout-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="glass w-full max-w-lg rounded-3xl p-6 md:p-8">
        <div className="flex items-start justify-between">
          <div><span className="eyebrow">{svc.payment_type === "deposit" ? "Deposit" : svc.payment_type === "balance" ? "Remaining balance" : "One-time service"}</span>
            <h2 className="mt-1 font-display text-3xl uppercase text-chrome">{svc.name}</h2></div>
          <button type="button" onClick={onClose} data-testid="service-checkout-close" className="rounded-full p-2 text-neutral-400 hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>
        <p className="mt-3 text-sm text-neutral-400">{svc.desc}</p>
        <div className="mt-4 flex items-center justify-between rounded-2xl border border-white/15 bg-black/30 p-4">
          <span className="text-sm text-neutral-300">{svc.payment_type === "deposit" ? "$150 balance due before delivery · no automatic charge" : "Secure Stripe checkout · USD"}</span>
          <span className="font-display text-2xl text-white" data-testid="service-modal-price">${svc.price.toLocaleString()}</span>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="service-name" className={inputCls} />
          <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="service-email" className={inputCls} />
          <input placeholder="Company (optional)" value={form.company} onChange={set("company")} data-testid="service-company" className={inputCls} />
          <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="service-phone" className={inputCls} />
        </div>
        <button type="submit" disabled={busy} data-testid="service-checkout-pay" className="btn-gold mt-5 flex w-full items-center justify-center gap-2 rounded-full py-4 font-display text-lg uppercase disabled:opacity-60">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="h-4 w-4" /> Pay ${svc.price.toLocaleString()}</>}
        </button>
      </form>
    </div>
  );
}

const Card = ({ s, onBuy, children }) => (
  <div className={`glass flex flex-col rounded-3xl p-6 ${s.payment_type !== "full" ? "border border-white/20" : ""}`} data-testid={`service-card-${s.id}`}>
    <div className="font-mono-label text-[10px] uppercase tracking-widest text-neutral-400">
      {s.payment_type === "deposit" ? "Deposit option" : s.payment_type === "balance" ? "Balance payment · deposit clients only" : "Consulting · One-time"}
    </div>
    <h3 className="mt-3 font-cond text-2xl font-extrabold uppercase leading-tight text-white">{s.name}</h3>
    <div className="mt-2 font-display text-3xl text-white" data-testid={`service-price-${s.id}`}>${s.price.toLocaleString()} <span className="text-base text-neutral-400">USD</span></div>
    <p className="mt-3 flex-1 text-sm text-neutral-400">{s.desc}</p>
    {children}
    {s.payment_link ? (
      <a href={s.payment_link} target="_blank" rel="noopener noreferrer" data-testid={`service-buy-${s.id}`} className="btn-gold mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-[12px]">
        Buy {s.name} — ${s.price.toLocaleString()} <ExternalLink className="h-3.5 w-3.5" />
      </a>
    ) : (
      <button onClick={() => onBuy(s)} data-testid={`service-buy-${s.id}`} className="btn-gold mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-[12px]">
        <ShieldCheck className="h-3.5 w-3.5" /> {s.payment_type === "deposit" ? `Pay $${s.price.toLocaleString()} deposit` : s.payment_type === "balance" ? `Pay $${s.price.toLocaleString()} balance` : `Buy ${s.name} — $${s.price.toLocaleString()}`}
      </button>
    )}
  </div>
);

export const ServiceButtons = () => {
  const [items, setItems] = useState([]);
  const [buying, setBuying] = useState(null);
  useEffect(() => { api.get("/service-catalog").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const main = items.filter((s) => s.payment_type === "full");
  const blueprintOpts = items.filter((s) => s.parent_id === "planning-blueprint");
  return (
    <section data-testid="services-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl uppercase text-white" data-testid="services-heading">Management & Consulting Services</h2>
          <p className="mt-2 max-w-2xl text-sm text-neutral-400">One-time prices in USD. Secure Stripe checkout. We contact you within one business day of purchase to arrange delivery.</p>
        </div>
        <Link to="/service-policy" data-testid="services-policy-link" className="font-cond text-sm font-bold uppercase tracking-wide text-white underline underline-offset-4">Service & Refund Policy</Link>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {main.map((s) => <Card key={s.id} s={s} onBuy={setBuying} />)}
        <div className="flex flex-col justify-center rounded-3xl border border-dashed border-neutral-700 p-6 text-sm text-neutral-400" data-testid="services-exclusions">
          <p className="font-cond text-base font-bold uppercase tracking-wide text-white">Scope note</p>
          <p className="mt-2">Prices cover EON's remote planning and coordination time only. <strong className="text-neutral-200">Third-party, on-site and talent costs are excluded</strong> — venues, vendors, production, travel and performers are quoted separately.</p>
          <p className="mt-3">Full refund before work begins · 24 hours' notice to reschedule. <Link to="/service-policy" className="text-white underline">Read the policy</Link>.</p>
        </div>
      </div>
      {blueprintOpts.length > 0 && (
        <div className="mt-10 rounded-3xl border border-white/15 p-6" data-testid="blueprint-split-pay">
          <h3 className="font-display text-xl uppercase text-white">Planning Blueprint — split payment option</h3>
          <p className="mt-2 max-w-3xl text-sm text-neutral-400">Prefer not to pay the $650 up front? Pay a <strong className="text-neutral-200">$500 deposit</strong> to start, then the <strong className="text-neutral-200">$150 remaining balance</strong> before delivery. Total stays $650. Nothing is charged automatically. The balance is <em>not</em> the $150 Additional Blueprint Revision service.</p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">{blueprintOpts.map((s) => <Card key={s.id} s={s} onBuy={setBuying} />)}</div>
        </div>
      )}
      {buying && <ServiceCheckoutModal svc={buying} onClose={() => setBuying(null)} />}
    </section>
  );
};
